<script setup lang="ts">
import type { defaultSideItemProps } from '@/types/appTypes'

const props = withDefaults(defineProps<defaultSideItemProps>(), {})

const app = useNuxtApp()
const storeIndex = useStoreIndex()
const storeUser = useStoreUser()

const pageData = { delay: 200 }

const refData = reactive({ isOnScreen: false })

const onIntersect = (isIntersecting, entries, observer) => {
  refData.isOnScreen = entries?.[0]?.isIntersecting
}

const coverAnim = computed(() => {
  return refData.isOnScreen ? 'openImage' : ''
})

const onClickDetail = () => {
  const to = `/present/${props.item.id}`
  if (storeUser.state.isLogin) {
    navigateTo(to)
  } else {
    storeUser.setLoginRedirectPath(to)
    navigateTo({ name: 'login' })
  }
}
</script>

<template>
  <div class="c-presentTopItem">
    <div
      v-intersect="onIntersect"
      class="c-presentTopItem_imageGroup"
    >
      <div
        class="c-presentTopItem_image u-hoverZoom"
        v-on:click="onClickDetail()"
      >
        <CommonImage
          class="c-presentTopItem_imageGroup_image"
          v-bind:src="storeIndex.getArticleEyeCatchImageURL(item.eye_catch_image_object)"
          error-src="/images/noimage.svg"
          v-bind:alt="item.title"
          height="170"
          object-fit="contain"
          is-hover-effect
        />
        <div
          class="c-presentTopItem_imageGroup_coverImage"
          v-bind:class="['a' + pageData.delay, coverAnim]"
        />
      </div>
    </div>
    <div
      v-intersect="onIntersect"
      class="c-presentTopItem_data"
    >
      <div
        class="c-presentTopItem_data_name u-overflowBreakAll u-animationText_name"
        v-bind:class="{ 'u-animationText': refData.isOnScreen }"
      />
      <div
        class="c-presentTopItem_data_detail u-animationText_detail c-clampLines is-clampline3"
        v-bind:class="{ 'u-animationText': refData.isOnScreen }"
      >
        {{ item.title }}
      </div>
      <div
        class="c-presentTopItem_data_duration u-animationText_duration"
        v-bind:class="{ 'u-animationText': refData.isOnScreen }"
      >
        {{ app.$getPeriodLabel(item.term_from, item.term_to) }}
      </div>
      <div
        v-if="item.form_date_to"
        class="c-presentTopItem_data_deadLine u-animationText_deadLine"
        v-bind:class="{ 'u-animationText': refData.isOnScreen }"
      >
        【締切】{{ app.$getPeriodDayLabel(item.form_date_to) }}
      </div>
      <div
        v-bind:class="{ 'u-fadeIn': refData.isOnScreen }"
        v-on:click="onClickDetail()"
      >
        <div
          class="c-presentTopItem_data_detailButton"
        >
          応募する
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

@keyframes coverAnimation {
  from {
    left: 0;
    width: 100%;
    opacity: 1;
  }

  to {
    left: 100%;
    width: 0;
    opacity: 0.5;
  }
}

@keyframes moveRight {
  from {
    left: -50%;
    opacity: 0;
  }

  to {
    left: 0;
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes underLineNormal {
  from {
    left: 0;
    width: 100%;
  }

  to {
    left: 100%;
    width: 0;
  }
}

@keyframes underLineHover {
  from {
    left: 0;
    width: 0;
  }

  to {
    left: 0;
    width: 100%;
  }
}

.u-fadeIn {
  animation: fadeIn 2s backwards;
}

.u-animationText {
  animation: moveRight cubic-bezier(0.28, 0.7, 0.32, 1.02) 0.6s 0.3s backwards;
}

.c-presentTopItem {
  display: flex;
  width: 535px;
  padding: 20px 0;
  border-top: 1px solid var(--g_color_border_gray);
  border-bottom: 1px solid var(--g_color_border_gray);

  & + & {
    margin-left: 30px;

    @include responsive.mq_down(sp) {
      margin-left: 0;
    }
  }

  @include responsive.mq_down(sp) {
    width: 100%;
  }

  &_imageGroup {
    display: flex;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 156px;
    border: 1px solid var(--g_color_border_gray);
    background-color: var(--g_color_image_background_f1f4f6);

    &_image {
      width: 156px;
      cursor: pointer;
    }

    &_coverImage {
      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: #c7c7c7;

      @include responsive.mq_down(sp) {
        height: 100%;
      }

      &.openImage {
        animation: coverAnimation 300ms 0.3s ease forwards;

        &.a0 {
          animation-delay: 300ms;
        }

        &.a1 {
          animation-delay: 300ms;
        }
      }
    }
  }

  &_data {
    width: 344px;
    margin-left: 30px;
    color: var(--color_text_base);
    font-family: var(--g_font_family_NotoSansCJKjp);
    font-size: 14px;

    @include responsive.mq_down(sp) {
      flex: 1;
      width: 100%;
    }

    &_name {
      position: relative;
      color: var(--color_text_base);
      font-size: 14px;
      font-weight: bold;
    }

    &_detail {
      position: relative;
      margin-top: 7px;
      font-size: 16px;
      font-weight: bold;
      line-height: 1.5;
    }

    &_duration {
      position: relative;
      margin-top: 12px;
      font-weight: 400;
    }

    &_deadLine {
      position: relative;
      margin-top: 9px;
      margin-left: -7px;
      color: var(--g_color_text_c20000);
      font-weight: bold;

      @include responsive.mq_down(sp) {
        display: none;
      }
    }

    &_detailButton {
      display: inline-block;
      position: relative;
      margin-top: 10px;
      margin-left: 3px;
      color: var(--color_primary_link);
      font-family: var(--g_font_family_NotoSansCJKjp);
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;

      &::before {
        content: "\276F";
        margin-right: 5px;
      }

      &::after {
        content: "";
        display: block;
        position: absolute;
        bottom: 0;
        width: 0;
        height: 2px;
        animation: underLineNormal 350ms var(--easeInOutQuart both);
        background: var(--color_primary);
      }

      &:hover {
        &::after {
          animation: underLineHover 350ms var(--easeInOutQuart both);
        }
      }
    }
  }
}

.u-overflowBreakAll {
  overflow: hidden;
  word-break: break-all;
}
</style>
